/* global BigInt */
// BannerCounter.jsx
import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import socket from "../../socket";
import JumpingCoin from "../JumpingCoin/JumpingCoin";
import { useMediaQuery, useTheme } from "@mui/material";
import coinsSound from "../../assets/coins_sound.mp3";
import { getClientId } from "../../utils/clientId";
import {
  formatQubAmount,
  QUB_AMOUNT_DECIMALS,
  qubAmountToAtomicBigInt,
  atomicBigIntToQubAmount,
} from "../../utils/qubAmount";
import {
  GAME_REWARD_ANIMATION_EVENT,
  HARDCORE_GAUGE_ANIMATION_EVENT,
  FIRST_VIDEO_REWARD_ANIMATION_EVENT,
  FIRST_MOMENT_REWARD_ANIMATION_EVENT,
} from "../../utils/gameRewardAnimation";
import { getApiBase } from "../../utils/apiBase";

const API_BASE = getApiBase({ trailingSlash: true });

const SOCKET_URL =
  (process.env.REACT_APP_SOCKET_URL || "http://localhost:3001")
    .replace(/\/+$/, "");

const SOCKET_PATH =
  process.env.REACT_APP_SOCKET_PATH || "/api/socket.io";

let socketInstance = null;
const ANIMATION_STEPS = 40;
const ANIMATION_DURATION_MS = 700;
const REWARD_SOUND_VOLUME = 0.25;
const BANNER_COUNTER_CACHE_PREFIX = "qubiq_banner_counter_cache:";

const getCounterCacheKey = (id) =>
  `${BANNER_COUNTER_CACHE_PREFIX}${String(id || "").trim()}`;

const readCachedCounterValue = (id) => {
  if (typeof window === "undefined") return null;
  const normalizedId = String(id || "").trim();
  if (!normalizedId) return null;

  try {
    const stored = window.localStorage.getItem(getCounterCacheKey(normalizedId));
    return stored ? String(stored) : null;
  } catch {
    return null;
  }
};

const persistCachedCounterValue = (ids, value) => {
  if (typeof window === "undefined") return;
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return;

  try {
    (Array.isArray(ids) ? ids : [ids]).forEach((id) => {
      const normalizedId = String(id || "").trim();
      if (!normalizedId) return;
      window.localStorage.setItem(getCounterCacheKey(normalizedId), normalizedValue);
    });
  } catch {}
};

const getWalletValueWithRewardOffset = (baseValue, rewardOffsetAtomic) =>
  atomicBigIntToQubAmount(
    qubAmountToAtomicBigInt(baseValue ?? "0") + rewardOffsetAtomic
  );

const splitWalletDisplay = (amount) => {
  const formatted = formatQubAmount(amount || "0.000000000000", {
    decimals: QUB_AMOUNT_DECIMALS,
    group: true,
  });
  const decimalIndex = formatted.indexOf(".");

  if (decimalIndex === -1) {
    return { muted: "", strong: formatted };
  }

  const integerPart = formatted.slice(0, decimalIndex).replace(/[^\d]/g, "");
  if (/[1-9]/.test(integerPart)) {
    return { muted: "", strong: formatted };
  }

  const fractionalPart = formatted.slice(decimalIndex + 1);
  const firstNonZeroIndex = fractionalPart.search(/[1-9]/);

  if (firstNonZeroIndex === -1) {
    return { muted: formatted, strong: "" };
  }

  const splitIndex = decimalIndex + 1 + firstNonZeroIndex;
  return {
    muted: formatted.slice(0, splitIndex),
    strong: formatted.slice(splitIndex),
  };
};

const BannerCounter = ({
  transparentBackground = false,
  compact = false,
  onValueChange = null,
  textColor = "#fff",
  showWalletLabel = true,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const clientId = getClientId();
  const cachedInitialValue = readCachedCounterValue(clientId);
  const [valueStr, setValueStr] = useState(cachedInitialValue);
  const [displayValueStr, setDisplayValueStr] = useState(cachedInitialValue);
  const [isOnline, setIsOnline] = useState(true);
  const animationRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const liveValueRef = useRef(null);
  const displayValueRef = useRef(null);
  const rewardOffsetAtomicRef = useRef(0n);
  const rewardAudioRef = useRef(null);
  const lastServerDeltaAtomicRef = useRef(0n);
  const lastServerUpdateAtRef = useRef(0);
  const [canonicalId, setCanonicalId] = useState(clientId);

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const walletDisplayParts = useMemo(
    () => splitWalletDisplay(displayValueStr),
    [displayValueStr]
  );

  useEffect(() => {
    liveValueRef.current = valueStr;
    if (valueStr != null) {
      persistCachedCounterValue([clientId, canonicalId], valueStr);
    }
    if (!isAnimatingRef.current) {
      setDisplayValueStr(
        getWalletValueWithRewardOffset(valueStr, rewardOffsetAtomicRef.current)
      );
    }
  }, [canonicalId, clientId, valueStr]);

  useEffect(() => {
    displayValueRef.current = displayValueStr;
  }, [displayValueStr]);

  useEffect(() => {
    if (typeof onValueChange !== "function") return;
    onValueChange(valueStr ?? null);
  }, [onValueChange, valueStr]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const hydrateCounter = async () => {
      try {
        const headers = { Accept: "application/json" };
        if (isSignedIn) {
          const token = await getToken().catch(() => null);
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }
        }

        const response = await fetch(
          `${API_BASE}counter?clientId=${encodeURIComponent(clientId)}`,
          {
            credentials: "include",
            headers,
            signal: controller.signal,
          }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.error || t("bannerCounter.loadError"));
        }
        if (cancelled) return;

        const nextValue = String(data?.value ?? "0.000000000000");
        const nextCanonicalId = String(data?.canonicalId || clientId).trim() || clientId;

        setCanonicalId(nextCanonicalId);
        setValueStr(nextValue);
        persistCachedCounterValue([clientId, nextCanonicalId], nextValue);
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    };

    hydrateCounter();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [clientId, getToken, isSignedIn, t]);

  useEffect(() => {
    const audio = new Audio(coinsSound);
    audio.preload = "auto";
    audio.volume = REWARD_SOUND_VOLUME;
    rewardAudioRef.current = audio;

    return () => {
      if (rewardAudioRef.current) {
        rewardAudioRef.current.pause();
        rewardAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!clientId) return;

    const subscribedId = canonicalId || clientId;

    const onConnect = () => {
      setIsOnline(true);
      socket.emit("counter:subscribe", { clientId: subscribedId });
    };

    const onDisconnect = () => {
      setIsOnline(false);
    };

    const onUpdate = (payload) => {
      if (payload?.value == null) return;

      const nextValue = String(payload.value);
      const previousBaseAtomic = qubAmountToAtomicBigInt(
        liveValueRef.current ?? "0"
      );
      const nextBaseAtomic = qubAmountToAtomicBigInt(nextValue);
      const serverDeltaAtomic =
        nextBaseAtomic > previousBaseAtomic
          ? nextBaseAtomic - previousBaseAtomic
          : 0n;
      lastServerDeltaAtomicRef.current = serverDeltaAtomic;
      lastServerUpdateAtRef.current = Date.now();

      if (
        rewardOffsetAtomicRef.current > 0n &&
        serverDeltaAtomic > 0n
      ) {
        const absorbedAtomic =
          serverDeltaAtomic >= rewardOffsetAtomicRef.current
            ? rewardOffsetAtomicRef.current
            : serverDeltaAtomic;
        rewardOffsetAtomicRef.current -= absorbedAtomic;
      }

      setValueStr(nextValue);
    };

    const animateRewardGain = (gain, forceAlreadyApplied = false) => {
      const gainAtomic = qubAmountToAtomicBigInt(gain);

      if (gainAtomic <= 0n) return;

      clearInterval(animationRef.current);
      isAnimatingRef.current = true;

      if (rewardAudioRef.current) {
        rewardAudioRef.current.pause();
        rewardAudioRef.current.currentTime = 0;
        rewardAudioRef.current.volume = REWARD_SOUND_VOLUME;
        rewardAudioRef.current.play().catch(() => {});
      }

      const liveAtomic = qubAmountToAtomicBigInt(
        liveValueRef.current ?? "0"
      );
      const currentDisplayAtomic = qubAmountToAtomicBigInt(
        displayValueRef.current ??
          getWalletValueWithRewardOffset(
            liveValueRef.current,
            rewardOffsetAtomicRef.current
          )
      );
      const serverAlreadyAppliedReward =
        forceAlreadyApplied ||
        (lastServerDeltaAtomicRef.current >= gainAtomic &&
          Date.now() - lastServerUpdateAtRef.current < 1500);

      let startAtomic = currentDisplayAtomic;
      let targetAtomic;

      if (serverAlreadyAppliedReward) {
        const preRewardAtomic =
          liveAtomic >= gainAtomic ? liveAtomic - gainAtomic : 0n;
        startAtomic =
          currentDisplayAtomic > preRewardAtomic
            ? preRewardAtomic
            : currentDisplayAtomic;
        targetAtomic = liveAtomic;
      } else {
        rewardOffsetAtomicRef.current += gainAtomic;
        targetAtomic = qubAmountToAtomicBigInt(
          getWalletValueWithRewardOffset(
            liveValueRef.current,
            rewardOffsetAtomicRef.current
          )
        );
      }
      let step = 0;

      animationRef.current = setInterval(() => {
        step += 1;
        const nextAtomic =
          startAtomic +
          ((targetAtomic - startAtomic) * BigInt(step)) / BigInt(ANIMATION_STEPS);

        setDisplayValueStr(atomicBigIntToQubAmount(nextAtomic));

        if (step >= ANIMATION_STEPS) {
          clearInterval(animationRef.current);
          animationRef.current = null;
          isAnimatingRef.current = false;
          setDisplayValueStr(atomicBigIntToQubAmount(targetAtomic));
        }
      }, ANIMATION_DURATION_MS / ANIMATION_STEPS);
    };

    const onRewardAnimation = (event) => {
      const gain = String(event?.detail?.gain ?? "").trim();
      animateRewardGain(gain, false);
    };

    const onFirstVideoRewardAnimation = (event) => {
      const gain = String(event?.detail?.gain ?? "").trim();
      animateRewardGain(gain, true);
    };

    const onFirstMomentRewardAnimation = (event) => {
      const gain = String(event?.detail?.gain ?? "").trim();
      animateRewardGain(gain, true);
    };

    const onHardcoreGaugeAnimation = (event) => {
      const gain = String(event?.detail?.gain ?? "").trim();
      const gainAtomic = qubAmountToAtomicBigInt(gain);

      if (gainAtomic <= 0n || !rewardAudioRef.current) return;

      rewardAudioRef.current.pause();
      rewardAudioRef.current.currentTime = 0;
      rewardAudioRef.current.volume = REWARD_SOUND_VOLUME;
      rewardAudioRef.current.play().catch(() => {});
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("counter:update", onUpdate);
    window.addEventListener(GAME_REWARD_ANIMATION_EVENT, onRewardAnimation);
    window.addEventListener(
      FIRST_VIDEO_REWARD_ANIMATION_EVENT,
      onFirstVideoRewardAnimation
    );
    window.addEventListener(
      FIRST_MOMENT_REWARD_ANIMATION_EVENT,
      onFirstMomentRewardAnimation
    );
    window.addEventListener(HARDCORE_GAUGE_ANIMATION_EVENT, onHardcoreGaugeAnimation);

    // ✅ important: si déjà connecté, on subscribe tout de suite
    if (socket.connected) {
      socket.emit("counter:subscribe", { clientId: subscribedId });
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("counter:update", onUpdate);
      window.removeEventListener(GAME_REWARD_ANIMATION_EVENT, onRewardAnimation);
      window.removeEventListener(
        FIRST_VIDEO_REWARD_ANIMATION_EVENT,
        onFirstVideoRewardAnimation
      );
      window.removeEventListener(
        FIRST_MOMENT_REWARD_ANIMATION_EVENT,
        onFirstMomentRewardAnimation
      );
      window.removeEventListener(HARDCORE_GAUGE_ANIMATION_EVENT, onHardcoreGaugeAnimation);
      clearInterval(animationRef.current);
      socket.emit("counter:unsubscribe", { clientId: subscribedId });
    };
  }, [clientId, canonicalId]);


  const coinSize = compact ? 16 : isMdUp ? 26 : isSmDown ? 18 : 22;
  const coinJump = compact ? 3 : isMdUp ? 6 : isSmDown ? 4 : 5;
  const canOpenWalletProfile = Boolean(user?.id);

  return (
    <div
      onClick={() => {
        if (!canOpenWalletProfile) return;
        navigate(`/profile/${encodeURIComponent(user.id)}?tab=wallet`);
      }}
      style={{
        background: transparentBackground ? "transparent" : "#181a21",
        color: textColor,
        padding: transparentBackground ? "0" : compact ? "4px 8px" : "6px 10px",
        display: "flex",
        alignItems: "center",
        gap: compact ? 5 : 8,
        borderRadius: transparentBackground ? 0 : 8,
        whiteSpace: "nowrap",
        overflow: "visible",
        width: "auto",
        minWidth: "max-content",
        maxWidth: "none",
        cursor: canOpenWalletProfile ? "pointer" : "default",
        boxSizing: "border-box",
        justifyContent: "space-between",
        flexShrink: 0,
      }}
    >
      {/* Bloc gauche : libellé + valeur */}
      <div
        className="banner-counter-text"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexGrow: 1,
          flexShrink: 1,
          minWidth: 0,
          gap: compact ? 4 : 6,
          overflow: "visible",
        }}
      >
        {showWalletLabel ? (
          <span
            className="wallet-text"
            style={{
              fontWeight: 500,
              fontSize: "1.2rem",
              display: "inline",
              flexShrink: 0,
            }}
          >
            {t("profile.wallet")} :
          </span>
        ) : null}

        <strong
          style={{
            fontSize: compact ? "0.78rem" : isSmDown ? "0.79rem" : "0.84rem",
            fontVariantNumeric: "tabular-nums lining-nums",
            fontFamily: "var(--qub-number-font)",
            lineHeight: 1.2,
            textAlign: "right",
            whiteSpace: "nowrap",
            overflow: "visible",
            textOverflow: "clip",
            flexShrink: 0,
            minWidth: "max-content",
          }}
          aria-live="polite"
        >
          <span style={{ color: textColor, opacity: 0.34 }}>
            {walletDisplayParts.muted}
          </span>
          <span style={{ color: textColor }}>
            {walletDisplayParts.strong}
          </span>
        </strong>
      </div>

      {/* Bloc droite : JumpingCoin + état */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          gap: compact ? 4 : 8,
        }}
      >
        <JumpingCoin trigger={displayValueStr} size={coinSize} height={coinJump} />
      </div>
    </div>
  );
};

export default BannerCounter;
